import { FiShare2 as _FiShare } from "@react-icons/all-files/fi/FiShare2.js";
import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Button, ButtonGroup, Flex, Menu, MenuDivider, MenuItem, Popover, Text, Tooltip } from 'core/components';
import { RemoveMenuItem } from 'core/form';
import { Classes, PopoverPosition } from '@blueprintjs/core';
import styled from 'styled-components';
import { BOARD_LABEL } from '@kentik/ui-shared/boards/boards';
import { showErrorToast } from 'core/components/toast';
import { getHashForObject } from 'app/stores/query/urlHash';
import { withQueryHash } from 'app/util/queryHashParam';
import ShareViewDialog from 'app/views/core/ShareViewDialog';
import { AddPanelButton } from './AddPanelButton';
import AddToBoardDialog from './AddToBoardDialog';
import { toHashPayload } from './udeQueryHash';
import { getQueryFieldsSignature } from './panels/ude/queryFieldsSignature';
const Wrapper = styled.div.withConfig({
  displayName: "BoardSubnavTools__Wrapper",
  componentId: "sc-1thgqpt-0"
})(["&&{.", "{min-height:26px;}}"], Classes.BUTTON);
function getSubnavControls({
  board,
  panel,
  form
}) {
  const {
    canEdit
  } = board;
  const isFormValid = form ? form.valid : true;
  const hasUnsavedChanges = panel ? panel.isDirty || !!form?.dirty : board.isDirty;
  const isReady = panel ? panel.isReady : true;
  const isUdePanel = panel?.get('type') === 'ude';
  const showSaveActions = !isUdePanel || !!panel?.queryModel?.hasRun;
  const formData = form?.getValues()?.config?.data;
  const appliedSignature = panel?.lastAppliedQuerySignature;
  const hasUnappliedQueryChanges = isUdePanel && !!form && appliedSignature !== undefined && getQueryFieldsSignature(formData) !== appliedSignature;
  const isSaving = !board.get('draft') && !board.isSilentSaving && (board.isRequestActive('creating') || board.isRequestActive('updating')) || !!panel && !panel.get('draft') && (panel.isRequestActive('creating') || panel.isRequestActive('updating'));
  return {
    canEdit,
    isUdePanel,
    hasUnsavedChanges,
    showSaveActions,
    hasUnappliedQueryChanges,
    isSaving,
    isSaveEnabled: isReady && isFormValid && hasUnsavedChanges && !hasUnappliedQueryChanges,
    isAddEnabled: isReady && isFormValid && !hasUnappliedQueryChanges
  };
}
const BoardSubnavTools = ({
  board,
  panel,
  form,
  showAddButton = true,
  onSave,
  onAddPanel,
  onCancel,
  onShowSettings
}) => {
  const history = useHistory();
  const location = useLocation();
  const [showAddToExistingDialog, setShowAddToExistingDialog] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [isSharing, setIsSharing] = useState(false);
  const isPanelView = !!panel;
  const {
    canEdit,
    isUdePanel,
    hasUnappliedQueryChanges,
    hasUnsavedChanges,
    isAddEnabled,
    isSaveEnabled,
    isSaving,
    showSaveActions
  } = getSubnavControls({
    board,
    panel,
    form
  });
  const basePath = history.location.pathname.startsWith('/v4/ude') ? '/v4/ude' : '/v4/library';
  const handleSave = async () => {
    if (hasUnappliedQueryChanges) {
      return;
    }
    if (panel && form) {
      const values = form.getValues();
      panel.safeSet(values);
    }
    await onSave();
  };
  const handleAddPanel = async type => {
    if (panel && form) {
      const values = form.getValues();
      panel.safeSet(values);
    }
    await onAddPanel(type);
  };
  const handleAddToBoard = async () => {
    if (panel && form) {
      const values = form.getValues();
      panel.safeSet(values);
    }
    const boardConfig = board.get('config');
    await board.save({
      config: {
        ...boardConfig,
        isDashboard: true
      }
    });
    history.push({
      pathname: `${basePath}/boards/${board.id}`,
      search: location.search
    });
  };
  const handleBackToBoard = () => {
    history.push({
      pathname: `${basePath}/boards/${board.id}`,
      search: location.search
    });
  };
  const handleRevertBoard = async () => {
    await board.discardDraftPanels();
  };
  const handleDuplicate = async () => {
    const duplicateBoard = board.duplicate({
      save: false
    });
    await duplicateBoard.save();
    history.push(duplicateBoard.navigatePath, {
      isEditing: true
    });
  };
  const handleRemove = async () => {
    await board.destroy();
    history.push('/v4/library');
  };
  const handleShare = async () => {
    const queryModel = panel?.queryModel;
    if (!queryModel) {
      return;
    }
    setIsSharing(true);
    try {
      const payload = toHashPayload({
        query: queryModel.query,
        chart: queryModel.settings.chart
      });
      const hash = await getHashForObject(payload, undefined, {
        persist: true
      });
      setShareUrl(withQueryHash('/v4/ude', hash));
    } catch (error) {
      console.warn('Failed to mint UDE share hash', error);
      showErrorToast('Unable to create a share link. Please try again.');
    } finally {
      setIsSharing(false);
    }
  };
  const showActionsMenu = canEdit && !isPanelView;
  const showAddPanel = !isPanelView && showAddButton && canEdit;
  const showRevert = canEdit && !board.isNew && !isPanelView && board.isDirty;
  const actionsMenu = /*#__PURE__*/React.createElement(Menu, null, /*#__PURE__*/React.createElement(MenuItem, {
    icon: "cog",
    text: "Settings",
    onClick: onShowSettings
  }), /*#__PURE__*/React.createElement(MenuItem, {
    icon: "duplicate",
    text: "Duplicate",
    onClick: handleDuplicate
  }), board.canRemove && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MenuDivider, null), /*#__PURE__*/React.createElement(RemoveMenuItem, {
    model: board,
    entityName: board.typeLabel,
    text: "Delete",
    onRemove: handleRemove,
    confirmButtonText: "Delete"
  })));
  const saveMenu = /*#__PURE__*/React.createElement(Menu, null, /*#__PURE__*/React.createElement(MenuItem, {
    icon: "undo",
    text: "Revert",
    disabled: !board.isDirty || board.isRequestActive('destroying'),
    onClick: handleRevertBoard
  }));
  return /*#__PURE__*/React.createElement(Wrapper, null, /*#__PURE__*/React.createElement(Flex, {
    alignItems: "center",
    gap: 1
  }, showActionsMenu && /*#__PURE__*/React.createElement(Popover, {
    content: actionsMenu,
    position: PopoverPosition.BOTTOM_RIGHT,
    modifiers: {
      offset: {
        enabled: true,
        offset: '0,2px'
      }
    }
  }, /*#__PURE__*/React.createElement(Button, {
    rightIcon: "caret-down",
    minimal: true,
    text: "Actions",
    "data-testid": "BoardActionsButton"
  })), showAddPanel && /*#__PURE__*/React.createElement(AddPanelButton, {
    board: board,
    icon: "plus",
    minimal: true,
    text: "Add Panel",
    disabled: !isAddEnabled,
    onAddPanel: handleAddPanel
  }), isPanelView && board.singlePanelView && canEdit && showSaveActions && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ButtonGroup, null, /*#__PURE__*/React.createElement(Button, {
    text: `Add to ${BOARD_LABEL}`,
    disabled: !isAddEnabled,
    onClick: handleAddToBoard
  }), /*#__PURE__*/React.createElement(Popover, {
    content: /*#__PURE__*/React.createElement(Menu, null, /*#__PURE__*/React.createElement(MenuItem, {
      text: `Existing ${BOARD_LABEL}`,
      icon: "insert",
      onClick: () => setShowAddToExistingDialog(true)
    })),
    position: PopoverPosition.BOTTOM_RIGHT,
    modifiers: {
      offset: {
        enabled: true,
        offset: '0,2px'
      }
    },
    disabled: !isAddEnabled
  }, /*#__PURE__*/React.createElement(Button, {
    icon: "caret-down",
    disabled: !isAddEnabled,
    "aria-label": `Add to ${BOARD_LABEL} options`
  }))), panel && /*#__PURE__*/React.createElement(AddToBoardDialog, {
    isOpen: showAddToExistingDialog,
    panel: panel,
    onClose: () => setShowAddToExistingDialog(false)
  })), isPanelView && !board.singlePanelView && /*#__PURE__*/React.createElement(Button, {
    text: `Back to ${BOARD_LABEL}`,
    minimal: true,
    onClick: handleBackToBoard
  }), isPanelView && isUdePanel && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, {
    icon: _FiShare,
    minimal: true,
    text: "Share",
    disabled: !panel?.queryModel?.hasRun,
    loading: isSharing,
    onClick: handleShare
  }), shareUrl && /*#__PURE__*/React.createElement(ShareViewDialog, {
    isOpen: true,
    hideEmail: true,
    hideSubscription: true,
    legacyLinkShare: true,
    privateShareUrl: shareUrl,
    onClose: () => setShareUrl(null)
  })), canEdit && showSaveActions && (isPanelView ? /*#__PURE__*/React.createElement(ButtonGroup, null, /*#__PURE__*/React.createElement(Tooltip, {
    content: hasUnappliedQueryChanges ? 'Run the query to save your changes' : 'You have unsaved changes',
    disabled: !hasUnappliedQueryChanges && !hasUnsavedChanges,
    position: PopoverPosition.BOTTOM
  }, /*#__PURE__*/React.createElement(Button, {
    text: "Save",
    icon: hasUnsavedChanges ? 'dot' : undefined,
    iconColor: "primary",
    disabled: !isSaveEnabled,
    loading: isSaving,
    onClick: handleSave
  })), !panel.isNew && !(board.singlePanelView && panel.hasOnlyDraftVersion) && /*#__PURE__*/React.createElement(Popover, {
    content: /*#__PURE__*/React.createElement(Menu, null, /*#__PURE__*/React.createElement(MenuItem, {
      icon: "undo",
      text: "Discard Changes",
      disabled: !panel.isDirty,
      onClick: onCancel
    })),
    position: PopoverPosition.BOTTOM_RIGHT,
    modifiers: {
      offset: {
        enabled: true,
        offset: '0,2px'
      }
    },
    disabled: !panel.isDirty
  }, /*#__PURE__*/React.createElement(Button, {
    rightIcon: "caret-down",
    disabled: !panel.isDirty
  }))) : /*#__PURE__*/React.createElement(ButtonGroup, null, /*#__PURE__*/React.createElement(Tooltip, {
    content: "You have unsaved changes",
    disabled: !hasUnsavedChanges,
    position: PopoverPosition.BOTTOM
  }, /*#__PURE__*/React.createElement(Button, {
    text: isSaving ? /*#__PURE__*/React.createElement(Text, {
      muted: true
    }, "Saving...") : 'Save',
    icon: hasUnsavedChanges && !isSaving ? 'dot' : null,
    fontWeight: 500,
    iconColor: "primary",
    disabled: !isSaveEnabled,
    onClick: handleSave,
    minWidth: 80
  })), /*#__PURE__*/React.createElement(Popover, {
    content: saveMenu,
    position: PopoverPosition.BOTTOM_RIGHT,
    modifiers: {
      offset: {
        enabled: true,
        offset: '0,2px'
      }
    },
    disabled: !showRevert
  }, /*#__PURE__*/React.createElement(Button, {
    rightIcon: "caret-down",
    disabled: !showRevert,
    "data-testid": "BoardSaveMenuButton"
  }))))));
};
export default observer(BoardSubnavTools);